import { styled } from "goober";
import {
  type ReactNode,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTweenedSeries } from "../../hooks/useTweenedSeries";
import { smoothPath, type Point } from "../../lib/smoothPath";
import { ChartMarker, ChartPill, ChartTooltip } from "../chartHover";
import { GhostPill } from "../GhostPill";
import { PressButton } from "../PressButton";

const VIEW_W = 960;
const VIEW_H = 200;
const PAD_X = 24;
const PAD_TOP = 16;
// Headroom below the value-0 baseline so the full line and its 3px dashed
// projection stay inside the fade masks (which end at VIEW_H) instead of the lower
// stroke half being masked away and reading as too thin.
const PAD_BOTTOM = 8;
const BASE_Y = VIEW_H - PAD_BOTTOM;
const PLOT_H = VIEW_H - PAD_TOP - PAD_BOTTOM;
const PLOT_W = VIEW_W - PAD_X * 2;
// Height reserved at the bottom of ChartArea for the axis labels — the SVG is
// calc(100% - AXIS_STRIP) tall — where the "Today"/hover pills bottom-align.
const AXIS_STRIP = 48;
const LINE = "var(--green-600)";
const LINE_DIM = "var(--green-400)";
const FUTURE = "var(--gray-400)";

// Empty-state skeleton: a fixed, muted curve standing in for "what this chart
// looks like with data", mirroring the ghost columns/rows on the other tabs.
// The path is constant, so build it once from a sample series.
const GHOST_COLOR = "var(--gray-400)";
const GHOST_LINE = [4, 6, 5, 8, 7, 6, 9, 11, 10, 13, 11, 14];
const GHOST_AXIS_PCT = [12, 31, 50, 69, 88];
const { curve: GHOST_CURVE, area: GHOST_AREA } = (() => {
  const max = Math.max(...GHOST_LINE);
  const gn = GHOST_LINE.length;
  const pts: Point[] = GHOST_LINE.map((v, i) => ({
    x: PAD_X + (i / (gn - 1)) * PLOT_W,
    y: BASE_Y - (v / max) * PLOT_H,
  }));
  const ext = [
    { x: 0, y: pts[0]!.y },
    ...pts,
    { x: VIEW_W, y: pts[pts.length - 1]!.y },
  ];
  const curve = smoothPath(ext);
  return { curve, area: `${curve}L${VIEW_W},${BASE_Y}L0,${BASE_Y}Z` };
})();

const ChartArea = styled("div")`
  position: relative;
  width: 100%;
  flex: 1;
  flex-shrink: 0;
  /* Shrink with the grid row rather than overflow — a min-height floor here
     pushes the axis labels past the card's bottom border. */
  min-height: 0;
`;

// An SVG with a viewBox has an intrinsic aspect ratio, so fixing only one axis
// lets the browser derive the other from it (pinning height alone makes the
// width a fixed viewBox-ratio value instead of filling). Pin both: width fills
// the container, height leaves AXIS_STRIP for the labels, and
// preserveAspectRatio="none" stretches the plot to fit.
const Svg = styled("svg")`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(100% - ${AXIS_STRIP}px);
  display: block;
  overflow: visible;
`;

// The placeholder line, softly faded at both edges so it reads as an ambient
// skeleton rather than a real, decisively-ending series.
const GhostSvg = styled(Svg)`
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    black ${PAD_X}px,
    black calc(100% - ${PAD_X}px),
    transparent
  );
  mask-image: linear-gradient(
    to right,
    transparent,
    black ${PAD_X}px,
    black calc(100% - ${PAD_X}px),
    transparent
  );
`;

// Same box as ChartPill (runs-graph edge label) so a hover/now pill lands
// exactly on the axis label baseline; only the background differs.
const AxisLabel = styled("div")`
  position: absolute;
  bottom: 0;
  transform: translateX(-50%);
  padding: 6px 8px;
  color: var(--text-secondary);
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 350;
  line-height: 12px;
  letter-spacing: -1%;
  white-space: nowrap;
  pointer-events: none;
`;

const SelectionTarget = styled(PressButton)`
  position: absolute;
  top: 0;
  bottom: 0;

  &:focus-visible {
    /* The marker and date pill provide the keyboard focus indicator. */
    outline: none;
  }
`;

export type TimeSeriesPoint = {
  key: number;
  value: number | null;
  projectedValue?: number | null;
  label: string;
};

export function TimeSeriesChart({
  points,
  labelStep = 1,
  projection,
  referenceLine,
  tooltip,
  tooltipWidth = 168,
  selection,
  minWidth,
  placeholder,
  overlayContainer,
}: {
  points: readonly TimeSeriesPoint[];
  labelStep?: number;
  projection?: {
    fromIndex: number;
    mode: "baseline" | "values";
    label?: string;
  };
  referenceLine?: number;
  tooltip: (index: number) => ReactNode;
  tooltipWidth?: number;
  selection?: {
    index: number;
    label: (index: number) => string;
    onSelect: (index: number) => void;
  };
  minWidth?: number;
  placeholder?: boolean;
  overlayContainer?: HTMLElement | null;
}) {
  const uid = useId();
  const gradId = `${uid}-fill`;
  const ghostGradId = `${uid}-ghost-fill`;
  const fadeGradId = `${uid}-fade-grad`;
  const fadeMaskId = `${uid}-fade`;
  const rightFadeGradId = `${uid}-rfade-grad`;
  const rightFadeMaskId = `${uid}-rfade`;
  const highlightClipId = `${uid}-highlight`;
  const chartRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const [hoveredKey, setHoveredKey] = useState<number | null>(null);
  const displayedTotals = useTweenedSeries(
    points.map((point) => point.value ?? 0),
    true,
    350
  );
  const displayedProjections = useTweenedSeries(
    projection?.mode === "values"
      ? points.map((point) => point.projectedValue ?? point.value ?? 0)
      : [],
    true,
    350
  );
  const n = points.length;
  const spacing = n > 1 ? PLOT_W / (n - 1) : PLOT_W;
  const xSvg = (i: number) =>
    n > 1 ? PAD_X + (i / (n - 1)) * PLOT_W : VIEW_W / 2;
  const xPct = (i: number) => (xSvg(i) / VIEW_W) * 100;
  const projectionFromIndex = projection?.fromIndex ?? n - 1;
  const hasFuture = projectionFromIndex < n - 1;
  const hasFutureBaseline = hasFuture && projection?.mode === "baseline";
  const lastActualIndex = hasFutureBaseline ? projectionFromIndex : n - 1;
  const nowX = projectionFromIndex >= 0 ? xSvg(projectionFromIndex) : 0;
  const max = Math.max(
    1,
    referenceLine ?? 0,
    ...displayedTotals,
    ...displayedProjections
  );
  const referenceY =
    referenceLine == null ? null : BASE_Y - (referenceLine / max) * PLOT_H;
  const y = (value: number) => BASE_Y - (value / max) * PLOT_H;
  const actualPoints: Point[] = points
    .slice(0, lastActualIndex + 1)
    .map((_, i) => ({ x: xSvg(i), y: y(displayedTotals[i] ?? 0) }));
  const extended = actualPoints.length
    ? [
        { x: 0, y: actualPoints[0]!.y },
        ...actualPoints,
        ...(hasFutureBaseline
          ? []
          : [{ x: VIEW_W, y: actualPoints.at(-1)!.y }]),
      ]
    : [];
  const curve = smoothPath(extended);
  const area = curve
    ? `${curve}L${hasFutureBaseline ? nowX : VIEW_W},${BASE_Y}L0,${BASE_Y}Z`
    : "";
  const forecastPoints =
    projection?.mode === "values"
      ? points.flatMap((point, i) => {
          if (i === projectionFromIndex) {
            return [{ x: xSvg(i), y: y(displayedTotals[i] ?? 0) }];
          }
          return i > projectionFromIndex && point.projectedValue != null
            ? [{ x: xSvg(i), y: y(displayedProjections[i] ?? 0) }]
            : [];
        })
      : [];
  const forecast =
    forecastPoints.length && points.at(-1)?.projectedValue != null
      ? smoothPath([
          ...(projectionFromIndex < 0
            ? [{ x: 0, y: forecastPoints[0]!.y }]
            : []),
          ...forecastPoints,
          { x: VIEW_W, y: forecastPoints.at(-1)!.y },
        ])
      : "";
  const hoveredIndex = points.findIndex((point) => point.key === hoveredKey);
  const hovered = points[hoveredIndex];
  const hoveredX = hovered ? xSvg(hoveredIndex) / VIEW_W : null;

  useLayoutEffect(() => {
    const chart = chartRef.current;
    const el = tooltipRef.current;
    const marker = markerRef.current;
    const pill = pillRef.current;
    const viewport = minWidth == null ? chart : chart?.parentElement;
    const overlay = overlayContainer ?? chart;
    if (
      !chart ||
      !el ||
      !marker ||
      !pill ||
      !viewport ||
      !overlay ||
      hoveredX == null
    )
      return;
    const position = () => {
      const chartBounds = chart.getBoundingClientRect();
      const viewportBounds = viewport.getBoundingClientRect();
      const overlayBounds = overlay.getBoundingClientRect();
      const markerX =
        chartBounds.left - overlayBounds.left + hoveredX * chartBounds.width;
      const left = viewportBounds.left - overlayBounds.left + 8;
      const right = viewportBounds.right - overlayBounds.left - 8;
      const tipW = el.offsetWidth;
      const preferred =
        markerX + 8 + tipW > right ? markerX - 8 - tipW : markerX + 8;
      el.style.left = `${Math.max(left, Math.min(preferred, right - tipW))}px`;
      marker.style.left = `${markerX}px`;
      pill.style.left = `${markerX}px`;
    };
    position();
    viewport.addEventListener("scroll", position, { passive: true });
    const observer = new ResizeObserver(position);
    observer.observe(viewport);
    return () => {
      viewport.removeEventListener("scroll", position);
      observer.disconnect();
    };
  }, [hoveredX, minWidth, overlayContainer]);

  const hoverOverlay = hovered && (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      <ChartMarker
        ref={markerRef}
        style={{
          left: `${xPct(hoveredIndex)}%`,
          top: 0,
          bottom: 0,
          background: "var(--text-primary)",
          transition: "left 150ms ease",
        }}
      />
      <ChartPill
        ref={pillRef}
        style={{
          left: `${xPct(hoveredIndex)}%`,
          bottom: 0,
          background: "var(--text-primary)",
          color: "var(--text-invert)",
          transition: "left 150ms ease",
        }}
      >
        {hovered.label}
      </ChartPill>
      <ChartTooltip
        ref={tooltipRef}
        role="tooltip"
        style={{ width: tooltipWidth, transition: "left 150ms ease" }}
      >
        {tooltip(hoveredIndex)}
      </ChartTooltip>
    </div>
  );

  if (placeholder) {
    return (
      <ChartArea aria-hidden>
        <GhostSvg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="none"
          fill="none"
        >
          <defs>
            <linearGradient id={ghostGradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={GHOST_COLOR} stopOpacity="0.15" />
              <stop offset="60%" stopColor={GHOST_COLOR} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={GHOST_AREA} fill={`url(#${ghostGradId})`} />
          <path
            d={GHOST_CURVE}
            stroke={GHOST_COLOR}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </GhostSvg>
        {GHOST_AXIS_PCT.map((left) => (
          <AxisLabel key={left} style={{ left: `${left}%` }}>
            <GhostPill style={{ display: "block", width: 28 }} />
          </AxisLabel>
        ))}
      </ChartArea>
    );
  }

  return (
    <ChartArea
      ref={chartRef}
      style={{ minWidth }}
      onMouseLeave={() => setHoveredKey(null)}
    >
      <Svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        fill="none"
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={LINE} stopOpacity="0.15" />
            <stop offset="60%" stopColor={LINE} stopOpacity="0" />
          </linearGradient>
          <linearGradient id={fadeGradId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop
              offset={`${(PAD_X / VIEW_W) * 100}%`}
              stopColor="white"
              stopOpacity="1"
            />
            <stop
              offset={`${((VIEW_W - PAD_X) / VIEW_W) * 100}%`}
              stopColor="white"
              stopOpacity="1"
            />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
          <mask
            id={fadeMaskId}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width={VIEW_W}
            height={VIEW_H}
          >
            <rect
              x="0"
              y="0"
              width={VIEW_W}
              height={VIEW_H}
              fill={`url(#${fadeGradId})`}
            />
          </mask>
          <linearGradient
            id={rightFadeGradId}
            gradientUnits="userSpaceOnUse"
            x1={nowX}
            y1="0"
            x2={VIEW_W}
            y2="0"
          >
            <stop offset="0%" stopColor="white" stopOpacity="1" />
            <stop offset="85%" stopColor="white" stopOpacity="1" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
          <mask
            id={rightFadeMaskId}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width={VIEW_W}
            height={VIEW_H}
          >
            <rect
              x={nowX}
              y="0"
              width={VIEW_W - nowX}
              height={VIEW_H}
              fill={`url(#${rightFadeGradId})`}
            />
          </mask>
          <clipPath id={highlightClipId}>
            <rect
              x={-spacing}
              y="0"
              width={spacing * 2}
              height={VIEW_H}
              style={{
                transform: `translateX(${xSvg(hoveredIndex >= 0 ? hoveredIndex : 0)}px)`,
                transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1) 50ms",
              }}
            />
          </clipPath>
        </defs>
        {referenceY != null && (
          <line
            x1="0"
            y1={referenceY}
            x2={VIEW_W}
            y2={referenceY}
            stroke="var(--red-400)"
            strokeWidth="2"
            strokeDasharray="6 4"
            strokeLinecap="round"
            mask={`url(#${fadeMaskId})`}
            vectorEffect="non-scaling-stroke"
            pointerEvents="none"
          />
        )}
        {hasFuture && (
          <g mask={`url(#${rightFadeMaskId})`}>
            {projection?.mode === "values" ? (
              <path
                d={forecast}
                stroke="var(--green-400)"
                strokeWidth="2"
                strokeDasharray="6 4"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            ) : (
              <line
                x1={nowX}
                y1={BASE_Y}
                x2={VIEW_W}
                y2={BASE_Y}
                stroke={FUTURE}
                strokeWidth="3"
                strokeDasharray="6 4"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </g>
        )}
        {curve && (
          <g mask={`url(#${fadeMaskId})`}>
            <path d={area} fill={`url(#${gradId})`} />
            <path
              d={curve}
              stroke={hovered ? LINE_DIM : LINE}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              style={{ transition: "stroke 150ms ease" }}
            />
            {hovered && (
              <path
                d={curve}
                stroke={LINE}
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                clipPath={`url(#${highlightClipId})`}
              />
            )}
          </g>
        )}
        {!selection &&
          points
            .slice(0, lastActualIndex + 1)
            .map((point, i) => (
              <rect
                key={point.key}
                x={xSvg(i) - spacing / 2}
                y="0"
                width={spacing}
                height={VIEW_H}
                fill="transparent"
                onMouseEnter={() => setHoveredKey(point.key)}
              />
            ))}
      </Svg>
      {points.map((point, i) =>
        i % labelStep === 0 ? (
          <AxisLabel key={point.key} style={{ left: `${xPct(i)}%` }}>
            {point.label}
          </AxisLabel>
        ) : null
      )}
      {selection &&
        points.map((point, i) => {
          const left = i === 0 ? 0 : xSvg(i) - spacing / 2;
          const right = i === n - 1 ? VIEW_W : xSvg(i) + spacing / 2;
          return (
            <SelectionTarget
              key={point.key}
              style={{
                left: `${(left / VIEW_W) * 100}%`,
                width: `${((right - left) / VIEW_W) * 100}%`,
              }}
              aria-label={selection.label(i)}
              aria-pressed={selection.index === i}
              onMouseEnter={() => setHoveredKey(point.key)}
              onMouseMove={() => setHoveredKey(point.key)}
              onFocus={() => setHoveredKey(point.key)}
              onBlur={() => setHoveredKey(null)}
              onPress={() => selection.onSelect(i)}
            >
              {null}
            </SelectionTarget>
          );
        })}
      {hasFuture && projection?.label && (
        <>
          <ChartMarker
            style={{ left: `${xPct(lastActualIndex)}%`, top: 0, bottom: 0 }}
          />
          <ChartPill
            style={{
              left: `${xPct(lastActualIndex)}%`,
              bottom: 0,
              color: "var(--text-secondary)",
              background: FUTURE,
            }}
          >
            {projection.label}
          </ChartPill>
        </>
      )}
      {overlayContainer
        ? createPortal(hoverOverlay, overlayContainer)
        : hoverOverlay}
    </ChartArea>
  );
}
